import { useRef, useState, type KeyboardEvent } from 'react';
import { ArrowRight, ArrowUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TeeButton } from '@/components/ui/tee-button';
import { TeeTextArea } from '@/components/ui/tee-textarea';

export const MAX_MESSAGE_LENGTH = 4000;
const COUNTER_FROM = 3600;
const LINE_HEIGHT_PX = 21;
const MAX_LINES = 5;

export interface AssistantComposerProps {
  isStreaming: boolean;
  disabled?: boolean;
  onSend: (text: string) => void;
  onStop: () => void;
  placeholder?: string;
  mobile?: boolean;
  onEscalate?: () => void;
  escalatedLabel?: string;
}

export function AssistantComposer({
  isStreaming,
  disabled = false,
  onSend,
  onStop,
  placeholder,
  mobile = false,
  onEscalate,
  escalatedLabel,
}: AssistantComposerProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);
  const hint = placeholder ?? t('assistant.chat.placeholder');

  const grow = () => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, LINE_HEIGHT_PX * MAX_LINES + 16)}px`;
  };

  const submit = () => {
    const text = value.trim();
    if (!text || isStreaming || disabled) return;
    onSend(text);
    setValue('');
    requestAnimationFrame(grow);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const sendDisabled = disabled || !value.trim();

  return (
    <div
      className={`flex flex-col gap-2 border-t border-[var(--border-default)] bg-[var(--background-card)] ${
        mobile
          ? 'px-3 pt-[10px] pb-[calc(14px+env(safe-area-inset-bottom))]'
          : 'px-[18px] pt-3 pb-[14px]'
      }`}
    >
      <TeeTextArea
        ref={ref}
        rows={mobile ? 1 : 2}
        showLabel={false}
        showHint={false}
        value={value}
        disabled={disabled}
        maxLength={MAX_MESSAGE_LENGTH}
        placeholder={hint}
        aria-label={hint}
        onChange={(e) => {
          setValue(e.target.value);
          grow();
        }}
        onKeyDown={onKeyDown}
      />
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {escalatedLabel ? (
            <span className="text-[12px] font-semibold text-[var(--text-content-sub-title)]">
              {escalatedLabel}
            </span>
          ) : (
            onEscalate && (
              <TeeButton
                variant="tertiary"
                size="s"
                disabled={disabled}
                onClick={onEscalate}
                text={t('assistant.composer.escalate')}
              />
            )
          )}
          <span className="text-[12px] font-semibold text-[var(--text-content-sub-title)]">
            {value.length >= COUNTER_FROM
              ? t('assistant.chat.counter', {
                  count: value.length,
                  max: MAX_MESSAGE_LENGTH,
                })
              : ''}
          </span>
        </div>
        {isStreaming ? (
          <TeeButton
            variant="secondary"
            size="s"
            onClick={onStop}
            text={t('assistant.chat.stop')}
          />
        ) : mobile ? (
          <TeeButton
            variant="primary"
            size="m"
            className="h-[44px] w-[44px] shrink-0 p-0"
            disabled={sendDisabled}
            onClick={submit}
            aria-label={t('assistant.composer.send')}
            text={<ArrowUp aria-hidden="true" className="h-5 w-5" />}
          />
        ) : (
          <TeeButton
            variant="primary"
            size="s"
            disabled={sendDisabled}
            onClick={submit}
            rightIcon={<ArrowRight aria-hidden="true" className="h-4 w-4" />}
            text={t('assistant.composer.send')}
          />
        )}
      </div>
    </div>
  );
}
